/** Filing lifecycle for the Batch Filing Review screen.
 *
 *  The batch grid used to know only about PRE-filing completeness ("does every required field have a
 *  value?"), so a policy that had already been filed stayed under "Ready to File" forever. The backend
 *  now stamps each batch-review row with where it actually is in the filing lifecycle; this module is
 *  the single place that reads those fields.
 *
 *  DEFENSIVE BY DESIGN: every accessor tolerates the lifecycle fields being absent (an environment
 *  running an older internal-api) and falls back to the old completeness-only verdict, so the screen
 *  degrades to its previous behaviour instead of breaking.
 */

/** Where a policy is in the filing lifecycle. `notReady` / `ready` are pre-filing completeness;
 *  everything after that describes a submission that has already left the building. */
export type RowFilingStatus = 'notReady' | 'ready' | 'queued' | 'filed' | 'rejected' | 'error';

/** The minimum a row must carry for the lifecycle helpers — kept structural (rather than importing
 *  BatchReviewRow) so this module has no dependency on the page that renders it. */
export interface FilingLifecycleRow {
    /** Backend completeness verdict — the pre-lifecycle field, still the fallback. */
    ready?: boolean;
    /** Backend lifecycle stamp. Absent on older API builds. */
    filingStatus?: string | null;
    /** Clearinghouse confirmation number, once a submission exists. */
    submissionNumber?: string | number | null;
    /** ISO timestamp of when the submission was sent. */
    submittedAt?: string | null;
}

/** Tolerant mapping from whatever the backend calls a status to our closed set. Keys are compared
 *  after lower-casing and stripping every non-letter, so `Awaiting_State`, `awaiting-state` and
 *  `AWAITING STATE` all land on the same entry. */
const STATUS_ALIASES: Record<string, RowFilingStatus> = {
    notready: 'notReady',
    incomplete: 'notReady',
    ready: 'ready',
    readytofile: 'ready',
    queued: 'queued',
    submitted: 'queued',
    pending: 'queued',
    awaitingstate: 'queued',
    awaitingstateapproval: 'queued',
    filed: 'filed',
    accepted: 'filed',
    complete: 'filed',
    rejected: 'rejected',
    declined: 'rejected',
    error: 'error',
    failed: 'error',
    conflict: 'error',
};

function normalizeKey(raw: string): string {
    return raw.toLowerCase().replace(/[^a-z]/g, '');
}

/** The row's lifecycle position. Unknown / missing backend values fall back to the completeness
 *  verdict, which is exactly the behaviour this screen had before the lifecycle existed. */
export function rowFilingStatus(row: FilingLifecycleRow): RowFilingStatus {
    const raw = row.filingStatus;
    if (typeof raw === 'string' && raw.trim()) {
        const hit = STATUS_ALIASES[normalizeKey(raw)];
        if (hit) return hit;
    }
    return row.ready ? 'ready' : 'notReady';
}

/** Whether this row may be selected and included in a filing submission. A policy that is already
 *  `queued` or `filed` must never be re-sent — that is how you double-file a policy with the state. */
export function isRowFilable(row: FilingLifecycleRow): boolean {
    return rowFilingStatus(row) === 'ready';
}

/** Whether the row has left the building (submitted or accepted). */
export function isRowSubmitted(row: FilingLifecycleRow): boolean {
    const status = rowFilingStatus(row);
    return status === 'queued' || status === 'filed';
}

/** The FIVE statuses MISC (Kelly, 2026-07-29) named as the filer-facing vocabulary.
 *
 *  ⚠️ THIS IS A DISPLAY SET, NOT THE BACKEND ENUM. The backend keeps SIX (`RowFilingStatus`), because
 *  `rejected` and `error` are genuinely different facts:
 *    rejected — the state reviewed the filing and refused it. Nothing is with the state.
 *    error    — OUR transport failed. The filing may or may not have reached the state.
 *  They collapse to one LABEL because a filer's next move starts the same way for both (open the row,
 *  read the reason), but they must never collapse to one MEANING — so `statusTooltip` spells out the
 *  different next step, and the row still prints the clearinghouse's own words (`filingStatusReason`).
 *  Collapsing in the database instead would have destroyed the distinction for the reconcile job and the
 *  SOX evidence trail, which is why the enum is untouched. */
export type DisplayFilingStatus = 'notReady' | 'ready' | 'queued' | 'filed' | 'needsAttention';

/** Backend status → the five-status display set. The ONLY place the six-to-five collapse happens. */
export function displayFilingStatus(status: RowFilingStatus): DisplayFilingStatus {
    return status === 'rejected' || status === 'error' ? 'needsAttention' : status;
}

export type LifecycleTab = 'notReady' | 'ready' | 'queued' | 'filed' | 'needsAttention' | 'all';

export interface LifecycleTabDef {
    value: LifecycleTab;
    label: string;
    /** Chip colours for the count pill on the tab. */
    countBg: string;
    countFg: string;
}

/** Tab strip, in lifecycle order: before filing → in flight → done → problems → everything. */
export const LIFECYCLE_TABS: LifecycleTabDef[] = [
    { value: 'notReady', label: 'Not Ready', countBg: '#fdecea', countFg: '#c62828' },
    { value: 'ready', label: 'Ready to File', countBg: '#e6f4ea', countFg: '#1e7e34' },
    { value: 'queued', label: 'Pending State Approval', countBg: '#e8f1fb', countFg: '#1565c0' },
    { value: 'filed', label: 'Filed', countBg: '#eef2f6', countFg: '#334155' },
    { value: 'needsAttention', label: 'Needs Attention', countBg: '#fff4e0', countFg: '#b26a00' },
    { value: 'all', label: 'All', countBg: '#eef2f6', countFg: '#334155' },
];

/** Plain-language definition + WHAT ACTION IS REQUIRED, for every status and every queue tab (Kelly's
 *  checklist #3). Two rules the copy follows deliberately:
 *
 *   1. Each string says what the status MEANS and then what the filer must DO — including "nothing".
 *      "Pending State Approval" exists precisely because the old "Awaiting state" left filers unsure
 *      whether they were being asked for something, so its copy leads with "No action needed."
 *   2. `rejected` and `error` share the "Needs Attention" label but NOT the same instruction. A rejected
 *      filing is not with the state and can be fixed and re-filed; a transport error may already have
 *      landed, so re-filing blind risks a duplicate filing with a real regulator. Tooltips are where
 *      that difference is preserved. */
const STATUS_TOOLTIPS: Record<RowFilingStatus, string> = {
    notReady: 'Not Ready — the documents were read but required values are still missing or invalid '
        + '(for example the policy number, premium or a required fee). Action: open the row, fill in '
        + 'the highlighted fields, then it moves to Ready to File.',
    ready: 'Ready to File — reviewed and complete; every required value is present. Action: select the '
        + 'row and click File Policies when you are ready to submit it to the state.',
    queued: 'Pending State Approval — submitted to the state and waiting on their response. '
        + 'No action needed. It stays here, visible, until the state answers; the status then moves to '
        + 'Filed or Needs Attention on its own.',
    filed: 'Filed — the state accepted this filing and it is recorded in the Filing Queue. '
        + 'No action needed.',
    rejected: 'Needs Attention — the state reviewed this filing and refused it, so it is NOT on file. '
        + 'Action: read the reason shown on the row, correct it, then file again.',
    error: 'Needs Attention — the submission could not be completed (a connection or credential '
        + 'problem on our side). It may or may not have reached the state. Action: check the state '
        + 'portal before filing again, so the policy is not filed twice.',
};

/** The hover text for a row's status chip. */
export function statusTooltip(status: RowFilingStatus): string {
    return STATUS_TOOLTIPS[status] || STATUS_TOOLTIPS.notReady;
}

/** The hover text for a lifecycle QUEUE tab. `needsAttention` covers both failure kinds, so its tab
 *  copy names both rather than inheriting one of them. `all` describes the queue itself. */
const TAB_TOOLTIPS: Record<LifecycleTab, string> = {
    notReady: STATUS_TOOLTIPS.notReady,
    ready: STATUS_TOOLTIPS.ready,
    queued: STATUS_TOOLTIPS.queued,
    filed: STATUS_TOOLTIPS.filed,
    needsAttention: 'Needs Attention — filings the state refused, plus submissions that failed to send. '
        + 'Action: open each row and read the reason. A refused filing can be corrected and re-filed; '
        + 'a failed send should be checked against the state portal first, in case it already landed.',
    all: 'Every transaction in this batch, whatever its status.',
};

export function tabTooltip(tab: LifecycleTab): string {
    return TAB_TOOLTIPS[tab] || '';
}

export function matchesLifecycleTab(row: FilingLifecycleRow, tab: LifecycleTab): boolean {
    if (tab === 'all') return true;
    const status = rowFilingStatus(row);
    if (tab === 'needsAttention') return status === 'rejected' || status === 'error';
    return status === tab;
}

export interface StatusChipStyle {
    label: string;
    bg: string;
    fg: string;
    /** True for the two pre-filing states, where the required-fields meter is still meaningful. */
    preFiling: boolean;
}

/** One place deciding how each lifecycle state READS to a filer, in MISC's five-status vocabulary.
 *
 *  "Pending State Approval" is deliberately never shortened to "Filed": those are different facts and
 *  conflating them is what made the old confirmation dialog lie. `rejected` and `error` share the
 *  "Needs Attention" label per Kelly's spec — see DisplayFilingStatus for why the two stay distinct
 *  underneath, and statusTooltip for the different action each one asks for. */
export function statusChipStyle(status: RowFilingStatus): StatusChipStyle {
    switch (status) {
        case 'ready':
            return { label: 'Ready to File', bg: '#e6f4ea', fg: '#1e7e34', preFiling: true };
        case 'queued':
            return { label: 'Pending State Approval', bg: '#e8f1fb', fg: '#1565c0', preFiling: false };
        case 'filed':
            return { label: 'Filed', bg: '#eef7ee', fg: '#2e7d32', preFiling: false };
        case 'rejected':
        case 'error':
            return { label: 'Needs Attention', bg: '#fff4e0', fg: '#b26a00', preFiling: false };
        case 'notReady':
        default:
            return { label: 'Not Ready', bg: '#fdecea', fg: '#c62828', preFiling: true };
    }
}

/** Escapes text before it is interpolated into a Swal `html` body. Server-supplied strings
 *  (clearinghouse messages, client names, block reasons) all go through this. */
export function escapeHtml(value: unknown): string {
    return String(value ?? '').replace(/[<>&"]/g, (c) => (
        { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c] as string
    ));
}

// ---------------------------------------------------------------------------
// submit-batch response contract
// ---------------------------------------------------------------------------

/** What `POST /AutomaticFiling/{state}/submit-batch` returns in `data`. Every field is optional:
 *  older builds return a thinner body and we must not crash on the difference. */
export interface SubmitBatchData {
    apiSubmitEnabled?: boolean;
    outcome?: 'filed' | 'queued' | 'rejected' | 'conflict' | 'error' | string;
    submissionNumber?: string | number | null;
    submissionStatus?: string | null;
    filed?: number[];
    queued?: number[];
    dropped?: { customerPolicyId: number; reason?: string }[];
    blocked?: { customerPolicyId: number; clientName?: string; reason?: string }[];
    messages?: string[];
    /** Build/validation failure shape (pre-existing contract, still emitted). */
    report?: { errors?: string[] };
    /** Legacy count fallback. */
    policyCount?: number;
}

/** The normalized outcome of one state's submit call, including the client-side-only `manual` and
 *  `transport` cases the backend can never report on itself. */
export type StateSubmitOutcome = 'filed' | 'queued' | 'rejected' | 'conflict' | 'error' | 'manual' | 'transport';

export interface StateSubmitResult {
    stateName: string;
    segment: string;
    /** customerPolicyIds we asked this state to file. */
    requestedIds: number[];
    outcome: StateSubmitOutcome;
    httpStatus: number | null;
    data: SubmitBatchData;
    /** The most specific human-readable reasons available for a failure. */
    errors: string[];
}

/** Vocabulary for the response's own `outcome` field, compared after the same lower-case/strip-
 *  non-letters normalization as the row statuses.
 *
 *  Deliberately SEPARATE from STATUS_ALIASES even though the words overlap: a row status has no
 *  `conflict` (it collapses to `error`), while a submit outcome does and must stay distinct. Kept
 *  exhaustive on purpose — an unrecognized verb falls through to inference, and an inference that
 *  defaults to "filed" is how a queued submission would be reported as a filing. */
const OUTCOME_ALIASES: Record<string, 'filed' | 'queued' | 'rejected' | 'conflict' | 'error'> = {
    filed: 'filed',
    accepted: 'filed',
    complete: 'filed',
    completed: 'filed',
    queued: 'queued',
    submitted: 'queued',
    pending: 'queued',
    awaiting: 'queued',
    awaitingstate: 'queued',
    awaitingapproval: 'queued',
    awaitingstateapproval: 'queued',
    awaitingreview: 'queued',
    inreview: 'queued',
    rejected: 'rejected',
    declined: 'rejected',
    refused: 'rejected',
    conflict: 'conflict',
    duplicate: 'conflict',
    alreadysubmitted: 'conflict',
    error: 'error',
    failed: 'error',
    failure: 'error',
};

/** Derive the outcome for one state's response.
 *
 *  ORDER MATTERS: the transport verdict is settled BEFORE the body's own `outcome` is trusted. A
 *  failed HTTP response that happens to carry `outcome: 'filed'` (a stale or optimistically-populated
 *  field) must never be reported as a success — reporting a filing that did not happen is the whole
 *  class of bug this screen exists to eliminate. Success therefore requires BOTH a successful response
 *  and a success outcome; a failed response can only pick WHICH kind of failure it was.
 *
 *  When the backend sends no `outcome` at all, the verdict is inferred from HTTP status and the
 *  filed/queued id arrays, so an older API still produces a truthful dialog rather than a blanket
 *  "Filed". */
export function deriveStateOutcome(
    res: { ok: boolean; status: number },
    body: { status?: boolean; message?: string } | null,
    data: SubmitBatchData,
): StateSubmitOutcome {
    if (res.status === 501 || data.apiSubmitEnabled === false) return 'manual';

    const explicit = typeof data.outcome === 'string' ? OUTCOME_ALIASES[normalizeKey(data.outcome)] : undefined;
    const httpFailed = !res.ok || body?.status === false;

    if (res.status === 409) return 'conflict';

    if (httpFailed) {
        // The request failed. The body may still say which flavour of failure it was, but it cannot
        // upgrade the result to a success.
        if (explicit === 'conflict') return 'conflict';
        if (explicit === 'error') return 'error';
        return 'rejected';
    }

    if (explicit) return explicit;

    // No explicit outcome: a non-empty `queued` with nothing filed is a queue, otherwise treat as filed.
    const queuedCount = Array.isArray(data.queued) ? data.queued.length : 0;
    const filedCount = Array.isArray(data.filed) ? data.filed.length : 0;
    if (queuedCount > 0 && filedCount === 0) return 'queued';
    return 'filed';
}

/** How many policies the state reported taking, split by kind.
 *
 *  `null` means "the backend did not report this at all" and is deliberately DIFFERENT from `0`.
 *  `filed: []` means ZERO policies were accepted — the previous code ran
 *  `filed.length || policyCount || requestedIds.length`, so an empty array fell straight through the
 *  `||` chain and the dialog announced the full requested count as filed, which is exactly the lie
 *  this branch exists to remove. A present-but-empty array must never fall back to anything. */
export function reportedCounts(data: SubmitBatchData): { filed: number | null; queued: number | null } {
    return {
        filed: Array.isArray(data.filed) ? data.filed.length : null,
        queued: Array.isArray(data.queued) ? data.queued.length : null,
    };
}

/** Total policies the state actually took (filed + queued), or `null` when it reported no counts at
 *  all and we can only go on the outcome verb. Zero is a real, reportable answer. */
export function confirmedPolicyCount(data: SubmitBatchData): number | null {
    const { filed, queued } = reportedCounts(data);
    if (filed === null && queued === null) {
        return typeof data.policyCount === 'number' ? data.policyCount : null;
    }
    return (filed ?? 0) + (queued ?? 0);
}

/** Whether a nominally-successful result actually moved any policy. A `filed`/`queued` outcome whose
 *  reported count is a hard zero (everything blocked or dropped) is NOT a success and must not let the
 *  aggregate dialog title read "Filed to the state". `null` counts fall back to trusting the verb. */
export function isEmptySuccess(result: { outcome: StateSubmitOutcome; data: SubmitBatchData }): boolean {
    if (result.outcome !== 'filed' && result.outcome !== 'queued') return false;
    return confirmedPolicyCount(result.data) === 0;
}

/** The most specific failure reasons available, in priority order: the build report's own errors, then
 *  the clearinghouse's messages, then whatever the envelope said. */
export function failureReasons(
    data: SubmitBatchData,
    body: { message?: string } | null,
    httpStatus: number | null,
): string[] {
    const reportErrors = data.report?.errors;
    if (Array.isArray(reportErrors) && reportErrors.length) return reportErrors;
    if (Array.isArray(data.messages) && data.messages.length) return data.messages;
    if (body?.message) return [body.message];
    return [httpStatus != null ? `HTTP ${httpStatus}` : 'The filing request could not be completed.'];
}
